import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import FormattedDateRangeInput from '../../../components/ui/FormattedDateRangeInput';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import { exceedsMax, isTodayOrFutureIsoDate } from '../../../utils/caseFieldValidation';
import { validationDateFutureOnly } from '../../../utils/validationI18n';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { isOtherNextStep } from './attorneyGeneralHelpers';
import { agEnterPlaceholder, agSelectPlaceholder } from './attorneyGeneralPlaceholders';
import { minDeadlineDateForAttorneyGeneral, validateAttorneyGeneralStep } from './attorneyGeneralStepValidation';
import { useAttorneyGeneralLookups } from './useAttorneyGeneralLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_TEXT = 2000;
const MAX_REF = 255;

export default function AttorneyGeneralStep3IncidentDetailsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const { nextSteps } = useAttorneyGeneralLookups('step3');
  const minDeadlineDate = useMemo(() => minDeadlineDateForAttorneyGeneral(), []);
  const showNextStepOther = isOtherNextStep(draft.nextStepOfCaseName, draft.nextStepOfCaseId);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateAttorneyGeneralStep(3, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <AttorneyGeneralWizardShell currentStep={3} draft={draft}>
      <SectionCard title={f.sectionIncidentMatterDetails}>
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.subjectOfDispute}</label>
          <textarea
            id="ag-subject"
            className={inpClass(Boolean(errors.subjectOfDispute))}
            rows={4}
            value={draft.subjectOfDispute}
            onChange={(e) => {
              setDraft((d) => ({ ...d, subjectOfDispute: maxLen('subjectOfDispute', e.target.value, MAX_TEXT) }));
            }}
            placeholder={agEnterPlaceholder('Subject of the Dispute')}
          />
          <FieldError message={errors.subjectOfDispute} />
        </div>

        <div className="ccr-fg ccr-field-half" style={{ marginBottom: 14 }}>
          <label>{f.incidentOccurredDate}</label>
          <FormattedDateRangeInput
            from={draft.incidentDateFrom}
            to={draft.incidentDateTo}
            enableYmdViews
            placeholder={agEnterPlaceholder('Incident Occurred Date')}
            onChange={(from, to) => setDraft((d) => ({ ...d, incidentDateFrom: from, incidentDateTo: to }))}
          />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: showNextStepOther ? 14 : 0 }}>
          <div className="ccr-fg">
            <label>{f.nextStepOfCaseIfApplicable}</label>
            <WizardSelect
              value={draft.nextStepOfCaseId}
              placeholder={agSelectPlaceholder('Next Step of the Case (if Applicable)')}
              options={nextSteps.map((s) => ({ id: s.id, label: s.name }))}
              onChange={(id) => {
                const row = nextSteps.find((s) => s.id === id);
                const name = row?.name ?? '';
                const otherSelected = isOtherNextStep(name, row?.code ?? id);
                setDraft((d) => ({
                  ...d,
                  nextStepOfCaseId: id,
                  nextStepOfCaseName: name,
                  nextStepOfCaseOther: otherSelected ? d.nextStepOfCaseOther : '' }));
                if (!otherSelected) clearErr('nextStepOfCaseOther');
              }}
            />
          </div>
          <div className="ccr-fg">
            <label>{f.nextDateOfCase}</label>
            <FormattedDateInput
              id="ag-nextCaseDate"
              className={inpClass(Boolean(errors.nextCaseDate))}
              value={draft.nextCaseDate}
              minDate={minDeadlineDate}
              enableYmdViews
              placeholder={agEnterPlaceholder('Next Date of the Case')}
              onChange={(next) => {
                setDraft((d) => ({ ...d, nextCaseDate: next }));
                clearErr('nextCaseDate');
                if (next && !isTodayOrFutureIsoDate(next)) {
                  setErrors((e) => ({ ...e, nextCaseDate: validationDateFutureOnly() }));
                }
              }}
            />
            <FieldError message={errors.nextCaseDate} />
          </div>
        </div>

        {showNextStepOther ? (
          <div className="ccr-fg" style={{ marginBottom: 0 }}>
            <label>{f.nextStepOther}</label>
            <input
              id="ag-nextStepOther"
              className={inpClass(Boolean(errors.nextStepOfCaseOther))}
              type="text"
              value={draft.nextStepOfCaseOther}
              onChange={(e) => {
                setDraft((d) => ({ ...d, nextStepOfCaseOther: maxLen('nextStepOfCaseOther', e.target.value, MAX_REF) }));
                clearErr('nextStepOfCaseOther');
              }}
              placeholder={f.enterNextStepOther}
            />
            <FieldError message={errors.nextStepOfCaseOther} />
          </div>
        ) : null}
      </SectionCard>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={3}
        onBack={() => nav(attorneyGeneralWizardPath(2, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(attorneyGeneralWizardPath(4, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
